import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import PricingCard from "./PricingCard";
import { axiosInstanceV2 } from "../../../services/axios";

import "../subscription-plan.scss";

const PricingSection = ({
	billing,
	setBilling,
	plans = [],
	hideFreePlan = false,
}) => {
	const navigate = useNavigate();

	const subscription = useSelector(
		(state) => state.userInfo.userInfo?.data?.subscription
	);

	const isExistingUser = Boolean(subscription);

	const [showModal, setShowModal] = useState(false);
	const [loading, setLoading] = useState(false);

	// Filter plans

	const filteredPlans = useMemo(() => {
		return plans
			.filter((plan) => !hideFreePlan || plan.key !== "free")
			.filter((plan) => {
				if (plan.key === "free") return true;

				return plan.billing?.some(
					({ period }) => period === billing
				);
			});
	}, [plans, hideFreePlan, billing]);

	// Free Plan Modal

	const handleOpenFreeModal = () => {
		setShowModal(true);
	};

	const activateFreePlan = async () => {
		try {
			setLoading(true);

			const authToken = localStorage.getItem("authToken");

			const response = await axiosInstanceV2.get(
				"/subscription/start-free",
				{
					headers: {
						Authorization: `Bearer ${authToken}`,
					},
				}
			);

			if (response?.data?.success) {
				toast.success("Your Free Plan is Active");

				setShowModal(false);

				navigate("/organizationDetail");

				return;
			}

			if (
				response?.data?.message ===
				"Subscription already exists"
			) {
				toast.info("Free plan already active.");

				setShowModal(false);

				navigate("/organizationDetail");

				return;
			}

			toast.error(
				response?.data?.message ??
				"Failed to activate free plan"
			);
		} catch (error) {
			toast.error(
				error?.response?.data?.message ??
				"Failed to activate free plan"
			);
		} finally {
			setLoading(false);
		}
	};

	// Plan Click

	const handleSelectPlan = (plan) => {
		if (!isExistingUser) {
			navigate("/checkout", {
				state: {
					plan,
					billing,
				},
			});
		}
	};

	return (
		<>
			<div className="pricing-wrapper">

				{/* Billing Toggle */}

				<div className="billing-toggle">
					<button
						className={
							billing === "monthly" ? "active" : ""
						}
						onClick={() => setBilling("monthly")}
					>
						Monthly
					</button>

					<button
						className={
							billing === "yearly" ? "active" : ""
						}
						onClick={() => setBilling("yearly")}
					>
						Yearly
					</button>
				</div>

				{/* Cards */}

				<div className="pricing-grid">

					{!filteredPlans.length && (
						<div className="w-100 text-center py-5">
							No {billing} plans available.
						</div>
					)}

					{filteredPlans.map((plan) => {
						const billingDetails =
							plan.key === "free"
								? plan.billing?.[0] || null
								: plan.billing.find(
									(item) => item.period === billing
								);

						return (
							<PricingCard
								key={plan._id || plan.key}
								plan={plan}
								billing={billing}
								billingDetails={billingDetails}
								currentPlanKey={null}
								currentRazorpayPlanId={null}
								onOpenFreeModal={handleOpenFreeModal}
								onSelectPlan={handleSelectPlan}
							/>
						);
					})}
				</div>
			</div>

			{/* Free Plan Modal */}

			{showModal && (
				<div
					className="modal fade show"
					style={{
						display: "block",
						background: "rgba(0,0,0,.5)",
					}}
				>
					<div className="modal-dialog modal-dialog-top">
						<div className="modal-content">

							<div className="modal-header">
								<h5>Free Plan Activation</h5>

								<button
									className="btn-close"
									onClick={() => setShowModal(false)}
								/>
							</div>

							<div className="modal-body">
								<p className="fw-semibold">
									Your Free Plan is Ready 🎉
								</p>

								<p className="text-muted">
									Continue with the free plan to
									complete your organization setup.
								</p>
							</div>

							<div className="modal-footer">

								<button
									className="btn-cancel"
									onClick={() => setShowModal(false)}
								>
									Cancel
								</button>

								<button
									disabled={loading}
									className="btn-primary-modal"
									onClick={activateFreePlan}
								>
									{loading
										? "Loading..."
										: "Continue Setup"}
								</button>

							</div>

						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default PricingSection;